<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu
      :user="user"
      :isMenuOpen="isMenuOpen"
      @toggle-menu="toggleMenu"
      @close-menu="closeMenu"
      @logout="logout"
    />

    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <h1 class="text-xl font-bold">Facturación / Facturar Pagos</h1>
          <p class="text-gray-600 mb-4">Matrícula: {{ studentId }}</p>

          <div v-if="loading" class="flex justify-center my-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>

          <div v-else-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {{ error }}
          </div>

          <div v-else-if="tickets && tickets.length > 0" class="mb-6">
            <h2 class="text-lg font-semibold mb-2">Registro de Pagos para Facturación:</h2>
            
            <!-- Vista móvil: tarjetas individuales -->
            <div class="grid grid-cols-1 gap-4 sm:hidden">
              <div 
                v-for="(item, index) in sortedTickets" 
                :key="index"
                class="bg-white shadow overflow-hidden rounded-md p-4 border-l-4"
                :class="item.invoiced === 1 ? 'border-green-500' : 'border-orange-500 bg-orange-50'"
              >
                <div class="grid grid-cols-2 gap-y-2">
                  <div class="text-sm font-medium text-gray-500">Ticket:</div>
                  <div class="text-sm font-semibold">{{ item.ticket }}</div>

                  <div class="text-sm font-medium text-gray-500">Fecha de Pago:</div>
                  <div class="text-sm">{{ formatDate(item.paymentdate) }}</div>

                  <div class="text-sm font-medium text-gray-500">Método:</div>
                  <div class="text-sm">{{ item.method }}</div>

                  <div class="text-sm font-medium text-gray-500">Monto:</div>
                  <div class="text-sm font-semibold">${{ formatAmount(item.amount) }}</div>

                  <div class="text-sm font-medium text-gray-500">Estado:</div>
                  <div class="text-sm font-bold" :class="item.invoiced === 1 ? 'text-green-600' : 'text-orange-600'">
                    {{ item.invoiced === 1 ? 'Facturado' : 'Pendiente' }}
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Vista escritorio: tabla tradicional -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg hidden sm:block">
              <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Ticket</th>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Fecha de Pago</th>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Método</th>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Monto</th>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Estado</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 bg-white text-sm font-medium text-gray-900">
                  <tr 
                    v-for="(item, index) in sortedTickets" 
                    :key="index"
                    :class="item.invoiced === 1 ? 'bg-green-50' : 'bg-orange-50'"
                  >
                    <td class="px-6 py-4 text-sm font-semibold text-gray-900">
                      {{ item.ticket }}
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-center text-sm text-gray-900">
                      {{ formatDate(item.paymentdate) }}
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-center text-sm text-gray-900">
                      {{ item.method }}
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-center text-sm font-semibold text-gray-900">
                      ${{ formatAmount(item.amount) }}
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-center text-sm font-bold" :class="item.invoiced === 1 ? 'text-green-600' : 'text-orange-600'">
                      {{ item.invoiced === 1 ? 'Facturado' : 'Pendiente' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div v-else class="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            No hay pagos disponibles para facturar.
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import { getTicketsBilling } from "@/services/api";

export default {
  components: {
    SidebarMenu
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      tickets: null,
      loading: true,
      error: null,
      studentId: null
    };
  },
  computed: {
    // Ordenar los tickets por estado (pendientes primero) y luego por fecha de pago (más recientes primero)
    sortedTickets() {
      if (!this.tickets) return [];
      
      return [...this.tickets].sort((a, b) => {
        // Primero por estado (pendientes primero)
        if (a.invoiced !== b.invoiced) {
          return a.invoiced - b.invoiced;
        }
        // Si tienen el mismo estado, ordenar por fecha de pago (más recientes primero)
        return new Date(b.paymentdate) - new Date(a.paymentdate);
      });
    }
  },
  async created() {
    await this.getUserFromBackend();
  },
  methods: {
    // Método para formatear fechas en formato dd/mm/yyyy
    formatDate(dateString) {
      if (!dateString) return '—';
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    
    // Método para formatear montos con separadores de miles
    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00';
      return parseFloat(amount).toLocaleString('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    
    async getUserFromBackend() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;
        
        // Extraer el ID de estudiante
        if (this.user.email && this.user.email.match(/^a(\d+)@/)) {
          this.studentId = this.user.email.match(/^a(\d+)@/)[1];

          // Cargar datos con el ID extraído
          await this.loadData();
        } else {
          this.error = "No se pudo extraer el ID de estudiante del email.";
          this.loading = false;
        }
      } catch (error) {
        console.error("Error obteniendo usuario", error);
        this.$router.push('/login');
      }
    },
    
    async loadData() {
      try {
        const response = await getTicketsBilling({ idstudent: this.studentId });
        this.tickets = response.data;
        this.loading = false;
      } catch (error) {
        console.error("Error al obtener los tickets para facturación:", error);
        this.error = "Error al cargar los pagos para facturación. Por favor, intente nuevamente.";
        this.loading = false;
      }
    },
    
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    
    closeMenu() {
      this.isMenuOpen = false;
    },
    
    async logout() {
      try {
        // Obtener nuevo token CSRF
        await axios.get('/sanctum/csrf-cookie');

        // Esperar un momento para asegurar que la cookie se haya establecido
        await new Promise(resolve => setTimeout(resolve, 100));

        // Realizar la petición de logout
        const response = await axios.post('/logout', {}, {
          withCredentials: true,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        console.log(response);
        
        // Limpiar cookies locales
        document.cookie.split(";").forEach(function(c) { 
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        
        // Redirigir al login
        this.$router.push('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error.response?.data || error);
      }
    }
  }
};
</script>